import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ChevronLeft, Loader } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../redux/store';
import { getEmployeeTimeCardRecordDetail } from '../../redux/features/EmployeeTimeCard';
import { setTopNavNameTitle } from '../../redux/features/mobileLayout';

interface FieldValue {
  value_code: string;
  value_text: string;
}

// Add LocationState interface to handle employee_id from navigation
interface LocationState {
  employeeId?: string;
}

interface AttendanceTag {
  state: string;
  field_code: string;
  field_name: string;
  field_values: FieldValue[];
}

interface GroupedTags {
  [key: string]: {
    name: string;
    values: string[];
  };
}

const AttendanceTagsView: React.FC<{}> = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const { timecardRecordDayDetailsData, requestStatus } = useSelector(
    (state: RootState) => state.employeeTimeCardData
  );
  const loading = requestStatus === 'PENDING';

  useEffect(() => {
    dispatch(setTopNavNameTitle("Attendance Tags"));
    if (id) {
      dispatch(getEmployeeTimeCardRecordDetail({
        id: Number(id),
        employee_id: locationState?.employeeId ? Number(locationState.employeeId) : undefined,
        extra_requested_fields: ["attendance_tag_details", "attendance_tag_count"],
      }));
    }
  }, [dispatch, id, locationState?.employeeId]);

  const attendanceTags = timecardRecordDayDetailsData?.attendence_tags || [];

  const handleBackClick = () => {
    navigate(-1);
  };

  // Group tags by field code
  const groupedTags = attendanceTags.reduce((acc: GroupedTags, tag: AttendanceTag) => {
    if (!acc[tag.field_code]) {
      acc[tag.field_code] = {
        name: tag.field_name,
        values: []
      };
    }
    const values = tag.field_values
      .map(fv => fv.value_text)
      .filter(text => text && text !== "0" && text !== "");
    
    if (values.length > 0) {
      // If there are multiple values separated by commas, split them
      const splitValues = values.flatMap(v => v.split(','));
      acc[tag.field_code].values.push(...splitValues);
    }
    return acc;
  }, {});

  return (
    <div className="min-h-full bg-white md:w-1/2 mx-auto">
      <div className="hidden md:flex items-center justify-between px-4 py-4 border-b border-gray-100">
        <button
          type="button"
          onClick={handleBackClick}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ChevronLeft size={20} />
          <span>Back</span>
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Attendance Tags</h1>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader className="animate-spin" color="#2563eb" size={32} />
        </div>
      ) : (
        <div className="p-4">
          {Object.keys(groupedTags).length > 0 ? (
            <div className="space-y-4">
              {(Object.entries(groupedTags) as [string, { name: string; values: string[] }][])
                .filter(([_, data]) => data.values && data.values.length > 0) // Only show fields with values
                .map(([fieldCode, data]) => (
                <div 
                  key={fieldCode}
                  className="bg-white border rounded-lg overflow-hidden animate-fadeIn"
                  style={{ animationDelay: `${data.values.length * 50}ms` }}
                >
                  <div className="px-4 py-2 border-b bg-gray-50">
                    <div className="text-sm font-medium text-gray-900">{data.name}</div>
                  </div>
                  <div className="p-3">
                    <div className="flex flex-wrap gap-2">
                      {data.values.map((value: string, valueIndex: number) => (
                        <div 
                          key={`${fieldCode}-${valueIndex}`}
                          className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded-md border border-blue-100"
                        >
                          {value}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64">
              <span className="text-gray-500">No tags available</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AttendanceTagsView;
